import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Edit, SimpleForm, SelectInput, AutocompleteInput, required,
  useRecordContext, useTranslate, useNotify, useRedirect,
} from 'react-admin';
import { Grid, Stack } from '@mui/material';
import { httpClient, HttpError } from '@/utils/httpClient';
import { DOCUMENT_SIDE_TO_INT } from '@/config/constants';
import { FormPageTitle, FormSection, FormToolbar } from '@/components/shell';
import { MappingFieldsEditor, MappingHelp } from './MappingFieldsEditor';
import { BothReadOnlyView } from './BothReadOnlyView';
import { BothPreviewWatcher } from './BothPreviewPanel';
import { useDocumentSideChoices } from './documentSides';
import { PriorityResolutionDialog } from './PriorityResolutionDialog';
import type {
  BothCompositionConflictResponse,
  FieldConflict,
  PrioritizationOverrides,
} from './bothComposition.types';

const BACK_TO = '/kyc-config?tab=fields-mapping';

const FieldMappingForm: React.FC<{
  mappingFieldsRef: React.MutableRefObject<any[]>;
  kycFields: any[];
  setKycFields: (v: any[]) => void;
}> = ({ mappingFieldsRef, kycFields, setKycFields }) => {
  const record = useRecordContext();
  const t = useTranslate();
  // Operators cannot edit the auto-generated `Both` row directly.
  const sideChoices = useDocumentSideChoices(true);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [nationalities, setNationalities] = useState<any[]>([]);
  const [standardFields, setStandardFields] = useState<any[]>([]);
  const [mappingFields, setMappingFields] = useState<any[]>([]);

  useEffect(() => {
    httpClient.get<any[]>('/admin/document-type/GetAllDocumentTypes').then(r => setDocumentTypes(r.data));
    httpClient.get<any[]>('/admin/nationality/GetAllNationality').then(r => setNationalities(r.data));
    httpClient.get<any[]>('/admin/kyc-field/GetAllKYCFields').then(r => setKycFields(r.data));
    httpClient.get<any[]>('/admin/fields-mapping/GetStandardFields').then(r => setStandardFields(r.data));
  }, [setKycFields]);

  useEffect(() => {
    if (record?.mappingFields) {
      const fields = record.mappingFields.map((f: any) => ({
        id: f.id,
        standardFieldName: f.fieldName || f.standardFieldName || '',
        regex: f.regex || '',
        kycMappingField: (f.kycMappingField || []).map((k: any) => ({
          id: k.id,
          prefix: k.prefix || '',
          kycField: k.kycField || { id: k.kycFieldId || 0, name: '' },
          suffix: k.suffix || '',
        })),
      }));
      setMappingFields(fields);
    }
  }, [record]);

  const handleChange = (fields: any[]) => {
    setMappingFields(fields);
    mappingFieldsRef.current = fields;
  };
  mappingFieldsRef.current = mappingFields;

  const kycFieldNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const k of kycFields) m[k.id] = k.name;
    return m;
  }, [kycFields]);

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <FormSection title={t('screens.fieldMappings.documentInfo')} action={<MappingHelp />}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <AutocompleteInput source="nationalityResponse.id" label={t('screens.fieldMappings.nationality')}
              choices={nationalities.map(n => ({ id: n.id, name: n.name }))}
              validate={required()} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutocompleteInput source="documentTypeResponse.id" label={t('screens.fieldMappings.documentType')}
              choices={documentTypes.map(d => ({ id: d.id, name: d.name }))}
              validate={required()} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectInput source="documentSide" label={t('screens.fieldMappings.documentSide')}
              choices={sideChoices} validate={required()} fullWidth />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title={t('screens.fieldMappings.mappingFields')}>
        <MappingFieldsEditor
          mappingFields={mappingFields}
          onChange={handleChange}
          kycFields={kycFields}
          standardFields={standardFields}
        />
        <BothPreviewWatcher
          mappingFields={mappingFields}
          kycFieldNameById={kycFieldNameById}
          documentTypeSource="documentTypeResponse.id"
          nationalitySource="nationalityResponse.id"
        />
      </FormSection>
    </Stack>
  );
};

/** Switches between read-only `Both` view and the editable Front/Back form. */
const EditBody: React.FC<{
  mappingFieldsRef: React.MutableRefObject<any[]>;
  kycFields: any[];
  setKycFields: (v: any[]) => void;
  onSubmit: (data: any) => Promise<void>;
}> = ({ mappingFieldsRef, kycFields, setKycFields, onSubmit }) => {
  const record = useRecordContext();
  if (!record) return null;
  const sideInt = typeof record.documentSide === 'number'
    ? record.documentSide
    : DOCUMENT_SIDE_TO_INT[record.documentSide];
  if (sideInt === 3 || record.documentSide === 'Both') {
    return <BothReadOnlyView />;
  }
  return (
    <SimpleForm
      onSubmit={onSubmit}
      toolbar={<FormToolbar backTo={BACK_TO} showDelete alwaysEnableSave />}
    >
      <FieldMappingForm
        mappingFieldsRef={mappingFieldsRef}
        kycFields={kycFields}
        setKycFields={setKycFields}
      />
    </SimpleForm>
  );
};

export const FieldMappingEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const mappingFieldsRef = useRef<any[]>([]);
  const [kycFields, setKycFields] = useState<any[]>([]);
  const [pendingConflict, setPendingConflict] = useState<{ data: any; conflicts: FieldConflict[] } | null>(null);

  const kycFieldNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const k of kycFields) m[k.id] = k.name;
    return m;
  }, [kycFields]);

  const buildPayload = (data: any, overrides?: PrioritizationOverrides) => ({
    id: data.id,
    documentTypeId: data.documentTypeResponse?.id || data.documentTypeId,
    nationalityId: data.nationalityResponse?.id || data.nationalityId,
    documentSide: typeof data.documentSide === 'number'
      ? data.documentSide
      : (DOCUMENT_SIDE_TO_INT[data.documentSide] ?? 1),
    mappingFields: mappingFieldsRef.current.map((f) => ({
      ...(f.id ? { id: f.id } : {}),
      fieldName: f.standardFieldName || '',
      regex: f.regex || '',
      kycMappingField: (f.kycMappingField || []).map((k: any) => ({
        ...(k.id ? { id: k.id } : {}),
        prefix: k.prefix || '',
        kycFieldId: k.kycField?.id || null,
        suffix: k.suffix || '',
      })),
    })),
    ...(overrides ? { prioritizationOverrides: overrides } : {}),
  });

  const submit = async (data: any, overrides?: PrioritizationOverrides) => {
    const payload = buildPayload(data, overrides);
    try {
      await httpClient.put('/admin/fields-mapping/UpdateFieldMapping', payload);
      notify('fields-mapping.both.save_success', { type: 'success' });
      redirect(BACK_TO);
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 409) {
          const body = err.data as BothCompositionConflictResponse;
          setPendingConflict({ data, conflicts: body?.conflicts || [] });
          return;
        }
        if (err.status === 400) {
          const msg = (err.data as { message?: string })?.message || err.message;
          notify(msg, { type: 'error' });
          return;
        }
      }
      notify(err instanceof Error ? err.message : 'ra.notification.http_error', { type: 'error' });
    }
  };

  return (
    <>
      <Edit mutationMode="pessimistic" redirect={false} actions={<FormPageTitle mode="edit" />}>
        <EditBody
          mappingFieldsRef={mappingFieldsRef}
          kycFields={kycFields}
          setKycFields={setKycFields}
          onSubmit={(data) => submit(data)}
        />
      </Edit>
      <PriorityResolutionDialog
        open={!!pendingConflict}
        conflicts={pendingConflict?.conflicts || []}
        kycFieldNameById={kycFieldNameById}
        onCancel={() => setPendingConflict(null)}
        onConfirm={(overrides) => {
          const pending = pendingConflict;
          setPendingConflict(null);
          if (pending) submit(pending.data, overrides);
        }}
      />
    </>
  );
};
